import { ChangeEvent, memo, useCallback, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Select,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  TextField,
} from '@mui/material';

import LoadingButton from '@/components/LoadingButton';
import { Strong } from '@/components/rich-text-formatting';
import SmsBackupOptions from '@/legacy/components/user/twoFactorAuthentication/shared/SmsBackupOptionsFragment';
import {
  TwoFactorAuthentication,
  TwoFactorAuthenticationType,
} from '@/legacy/features/user/twoFactorAuthentication/twoFactorAuthentication';

import { LoginStep } from './useLoginPopupMultipleMethods';

function getCodeLabel(twoFactorAuthentication: TwoFactorAuthenticationType): string {
  switch (twoFactorAuthentication) {
    case TwoFactorAuthenticationType.Sms:
      return 'Enter SMS Code';

    case TwoFactorAuthenticationType.Otp:
      return 'Enter Authenticator Code';

    default:
      return null;
  }
}

interface Props {
  code: string;
  errorMessage: string;
  isLoading: boolean;
  isOpen?: boolean;
  onBackPressed: () => void;
  onCodeUpdated: (code: string) => void;
  onNextPressed: () => void;
  onProviderChange: (selectedTwoFactorAuthentication: TwoFactorAuthentication) => void;
  onRememberDeviceChange: (isChecked: boolean) => void;
  onResendCode: () => void;
  onCallMe: () => void;
  rememberDevice: boolean;
  selectedTwoFactorAuthentication: TwoFactorAuthentication;
  step: LoginStep;
  twoFactorAuthentications: Array<TwoFactorAuthentication>;
}

const LoginPopupMultipleMethodsPresentation = (props: Props): React.JSX.Element => {
  const { formatMessage } = useIntl();

  const {
    errorMessage,
    step,
    twoFactorAuthentications,
    isLoading,
    selectedTwoFactorAuthentication,
    rememberDevice,
    code,
    isOpen,
    onBackPressed,
    onNextPressed,
    onProviderChange,
    onCodeUpdated,
    onRememberDeviceChange,
    onResendCode,
    onCallMe,
  } = props;

  const codeInputRef = useRef<HTMLInputElement>(null);

  // If we get an error while entering code re-focus the code entry
  // Also re-focus once we finish loading two factor authentications
  useEffect(() => {
    if (
      step === LoginStep.VerifyAuthenticator &&
      (errorMessage || !isLoading) &&
      codeInputRef.current
    ) {
      codeInputRef.current.focus();
    }
  }, [errorMessage, step, isLoading]);

  const handleCodeUpdated = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => onCodeUpdated(e.target.value),
    [onCodeUpdated]
  );

  const handleRememberDeviceChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => onRememberDeviceChange(e.target.checked),
    [onRememberDeviceChange]
  );

  const handleProviderChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const matchingTwoFactorAuthentication = twoFactorAuthentications.find(
        (twoFactorAuthentication) => twoFactorAuthentication.id === e.target.value
      );

      onProviderChange(matchingTwoFactorAuthentication);
    },
    [onProviderChange, twoFactorAuthentications]
  );

  return (
    <Dialog open={isOpen} fullWidth maxWidth="xs">
      <DialogTitle>
        <FormattedMessage defaultMessage="Verify Two-Step Authentication" />
      </DialogTitle>
      <DialogContent>
        <Stepper activeStep={step} nonLinear orientation="vertical">
          <Step key={LoginStep.ChooseProvider}>
            <StepLabel>
              <FormattedMessage defaultMessage="Choose Your Two-Step Provider" />
            </StepLabel>

            <StepContent>
              <Box>
                <Select
                  variant="standard"
                  onChange={handleProviderChange}
                  value={selectedTwoFactorAuthentication?.id}
                  error={!!errorMessage}
                >
                  {twoFactorAuthentications.map((method) => (
                    <MenuItem value={method.id} key={method.id}>
                      {method.displayName}
                    </MenuItem>
                  ))}
                </Select>

                {errorMessage && <FormHelperText error>{errorMessage}</FormHelperText>}
              </Box>
            </StepContent>
          </Step>

          <Step key={LoginStep.VerifyAuthenticator}>
            <StepLabel>
              <FormattedMessage defaultMessage="Verify Two-Step Code" />
            </StepLabel>

            <StepContent>
              <Box mb={1}>
                <FormattedMessage
                  defaultMessage="Please enter the two-step verification code from <Strong>{twoFactorDisplayName}</Strong>:"
                  values={{
                    twoFactorDisplayName: selectedTwoFactorAuthentication?.displayName,
                    Strong,
                  }}
                />
              </Box>

              <Box>
                <TextField
                  id="code"
                  value={code}
                  onChange={handleCodeUpdated}
                  label={
                    selectedTwoFactorAuthentication &&
                    getCodeLabel(selectedTwoFactorAuthentication.type)
                  }
                  autoFocus
                  autoComplete="off"
                  disabled={isLoading}
                  error={!!errorMessage}
                  helperText={errorMessage}
                  inputRef={codeInputRef}
                  required
                  fullWidth
                  margin="normal"
                />

                {selectedTwoFactorAuthentication?.type === TwoFactorAuthenticationType.Sms && (
                  <SmsBackupOptions onCallMe={onCallMe} onResendCode={onResendCode} />
                )}
              </Box>

              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="rememberMe"
                      color="primary"
                      checked={rememberDevice}
                      disabled={isLoading}
                      onChange={handleRememberDeviceChange}
                    />
                  }
                  label={formatMessage({
                    defaultMessage: 'Remember this device for the next 30 days',
                  })}
                />
              </Box>
            </StepContent>
          </Step>
        </Stepper>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onBackPressed} type="button">
          <FormattedMessage defaultMessage="Back" />
        </Button>
        <LoadingButton color="primary" loading={isLoading} onClick={onNextPressed} type="button">
          <FormattedMessage defaultMessage="Next" />
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default memo(LoginPopupMultipleMethodsPresentation);
